import React, { useCallback, useEffect, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CarrierDisclaimer from 'tailwind-components/CarrierDisclaimer/CarrierDisclaimer';
import CarrierHeader from './CarrierHeader';
import CarrierNotesPanel from './CarrierNotesPanel';
import CarrierResourcesPanel from './CarrierResourcesPanel';
import CarrierSidebar from './CarrierSidebar';
import CarrierUpdatesPanel from './CarrierUpdatesPanel';
import ContentWithSidebar from 'tailwind-components/ContentWithSidebar/ContentWithSidebar';
import MainHeader from './MainHeader';
import SidebarPlaceholder from 'tailwind-components/ContentWithSidebar/SidebarPlaceholder';
import useBelowBreakpoint from 'hooks/useBelowBreakpoint';
import useNotifyTourPageReady from 'tailwind-components/PortalWalkthrough/useNotifyTourPageReady';
import { DISCLAIMER_PLACEMENTS } from 'helpers/factory/carrierFactory';
import { sortCarriersForOepSidebar } from 'helpers/oepUpdates';

const LAYOUT_BREAKPOINT = 'lg';

// main column + carriers column; the collapsed rail only fits the avatars
const SIDEBAR_GRID_EXPANDED =
  'lg:grid-cols-[minmax(0,1fr)_minmax(0,17rem)]';
const SIDEBAR_GRID_COLLAPSED = 'lg:grid-cols-[minmax(0,1fr)_4rem]';

/**
 * O'Neill-only "Open Enrollment Updates" page. Right sidebar lists every
 * carrier whose `Carrier_Audit_Consider_States` is "Yes" (i.e. operates
 * state-by-state) and isn't flagged `Hide_from_OEP_Updates`, with carriers
 * that actually have updates ranked first and the rest below (each group
 * alphabetical). Non-state-based carriers are out of scope for OEP updates
 * by product decision. Main pane shows the picked carrier's header, the
 * expansions/exits by state (with county detail), and the workdrive resources.
 * Filters the `carriersArray` already in redux to avoid a second fetch, and
 * syncs the current carrier with `?carrier=` in the URL so links land on a
 * specific carrier. If the query param is missing or invalid, the first
 * carrier is auto-selected so the page never opens empty.
 * @param getAppointmentsViewData - redux thunk that populates carriersArray
 * @param setLocalLoader - toggle for the withLocalLoader HOC overlay
 */
export default function OepUpdates({
  getAppointmentsViewData,
  setLocalLoader
}) {
  const cancelTokenSource = axios.CancelToken.source();
  const [carriersLoading, setCarriersLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isBelowLayout = useBelowBreakpoint(LAYOUT_BREAKPOINT);

  const { carriersArray } = useSelector(state => state.appointments);
  const user = useSelector(state => state.user.zohoData);
  const isOepWindowActive = !!user?.isOepWindowActive;
  const oepWindowStartDate = user?.oepWindowStartDate || '';

  // OEP updates only apply to carriers that operate state-by-state (the
  // `Carrier_Audit_Consider_States` picklist in Zoho). anything else doesn't
  // have meaningful state-level expansions/exits to publish.
  const oepCarriers = useMemo(
    () =>
      sortCarriersForOepSidebar(
        (carriersArray || []).filter(
          c => c.considerStates === 'Yes' && !c.oepHidden
        )
      ),
    [carriersArray]
  );

  const selectedCarrierName = searchParams.get('carrier');

  const currentCarrier = useMemo(
    () => oepCarriers.find(c => c.name === selectedCarrierName) || null,
    [oepCarriers, selectedCarrierName]
  );

  const setCarrierInUrl = useCallback(
    (nextName, { replace = true } = {}) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (nextName) next.set('carrier', nextName);
          else next.delete('carrier');
          return next;
        },
        { replace }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    // skip the fetch (and the loader toggle) when the OEP window is closed:
    // the component is about to return the closed-window placeholder anyway,
    // and firing the request would just show a spinner for data no one sees.
    if (!isOepWindowActive) {
      setLocalLoader(false);
      setCarriersLoading(false);
      return undefined;
    }
    getAppointmentsViewData(
      value => {
        setLocalLoader(value);
        setCarriersLoading(value);
      },
      cancelTokenSource,
      true
    );
    return () => cancelTokenSource.cancel('API Call Canceled');
  }, []);

  // auto-select the first carrier when nothing (or something invalid) is in the
  // url. runs after the fetch resolves so we don't clobber a valid link while
  // the list is still empty.
  useEffect(() => {
    if (carriersLoading) return;
    if (oepCarriers.length === 0) return;
    if (currentCarrier) return;
    setCarrierInUrl(oepCarriers[0].name);
  }, [carriersLoading, oepCarriers, currentCarrier, setCarrierInUrl]);

  useNotifyTourPageReady(carriersLoading);

  // out-of-window hard gate: when the OEP window is closed we don't even render
  // the sidebar/main shell, just a single placeholder. the sidebar MenuLink is
  // already hidden in this state, so this covers direct URL access and stale
  // bookmarks.
  if (!isOepWindowActive) {
    return (
      <div className='flex h-full w-full items-center justify-center p-8'>
        <SidebarPlaceholder
          text={buildClosedWindowMessage(oepWindowStartDate)}
        />
      </div>
    );
  }

  function handleCarrierSelect(name) {
    setCarrierInUrl(name);
    // on mobile the sheet stays open by default: close it so the picked carrier
    // becomes visible immediately
    if (isBelowLayout) setSidebarOpen(false);
  }

  // the mobile sheet already hides the list, so collapsing only applies inline
  const isCollapsed = sidebarCollapsed && !isBelowLayout;

  const mainContent = (
    <div
      data-tour='oep-updates-main'
      className='flex h-full min-h-0 w-full flex-col gap-4 overflow-y-auto rounded-xl bg-grey-lightest p-4 show-scrollbar lg:p-5'
    >
      <MainHeader
        isBelowLayout={isBelowLayout}
        currentCarrierName={currentCarrier?.name}
        onOpenSidebar={() => setSidebarOpen(true)}
      />
      {renderMainBody({ carriersLoading, oepCarriers, currentCarrier })}
    </div>
  );

  return (
    <div className='w-full h-full'>
      <ContentWithSidebar
        layoutBreakpoint={LAYOUT_BREAKPOINT}
        sidebarSide='right'
        sidebarAsSheetOnMobile
        sidebarOpen={sidebarOpen}
        onSidebarOpenChange={setSidebarOpen}
        sidebarTitle='Carriers'
        desktopSidebarGridClassName={
          isCollapsed ? SIDEBAR_GRID_COLLAPSED : SIDEBAR_GRID_EXPANDED
        }
        mainContent={mainContent}
        sidebarContent={
          <CarrierSidebar
            carriers={oepCarriers}
            selectedName={currentCarrier?.name || null}
            onSelect={handleCarrierSelect}
            collapsed={isCollapsed}
            onCollapsedChange={isBelowLayout ? undefined : setSidebarCollapsed}
          />
        }
      />
    </div>
  );
}

// user-facing copy for when the agent lands on /oep-updates outside the
// configured OEP window. prefers the exact reopen date when available so
// agents know when to come back.
function buildClosedWindowMessage(oepWindowStartDate) {
  if (oepWindowStartDate) {
    return `The OEP Updates section is currently closed. It will reopen on ${oepWindowStartDate}.`;
  }
  return 'The OEP Updates section is currently closed.';
}

// picks what to render below the sticky header. kept out of the render tree
// so the branching stays flat (no nested ternaries per CLAUDE.md).
function renderMainBody({ carriersLoading, oepCarriers, currentCarrier }) {
  if (carriersLoading) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-3 py-16'>
        <Loader2
          aria-hidden
          className='size-8 shrink-0 animate-spin text-primary'
        />
        <p className='m-0 text-base font-medium text-gray-800'>
          Loading carriers...
        </p>
      </div>
    );
  }

  if (oepCarriers.length === 0) {
    return (
      <div className='flex min-h-[420px] flex-1'>
        <SidebarPlaceholder text='No carriers available for OEP updates.' />
      </div>
    );
  }

  if (!currentCarrier) {
    return (
      <div className='flex min-h-[420px] flex-1'>
        <SidebarPlaceholder text='Pick a carrier from the sidebar to view its OEP updates.' />
      </div>
    );
  }

  return (
    <>
      {showsOepDisclaimer(currentCarrier) && (
        <CarrierDisclaimer disclaimer={currentCarrier.disclaimer} />
      )}
      <CarrierHeader carrier={currentCarrier} />
      <CarrierNotesPanel carrier={currentCarrier} />
      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        <CarrierUpdatesPanel carrier={currentCarrier} />
        <CarrierResourcesPanel carrier={currentCarrier} />
      </div>
    </>
  );
}

// the carrier disclaimer is opt-in per surface: it only shows here when an
// admin ticked `OEP Updates Tab` in the Disclaimer_Placements picklist, same
// contract the sidebars and the states map already follow.
function showsOepDisclaimer(carrier) {
  return !!carrier?.disclaimer?.placements?.has(
    DISCLAIMER_PLACEMENTS.OEP_UPDATES_TAB
  );
}

OepUpdates.propTypes = {
  getAppointmentsViewData: propTypes.func.isRequired,
  setLocalLoader: propTypes.func.isRequired
};
